"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Trash2,
  Building2,
  Phone,
  Mail,
  User,
  Calendar,
  DollarSign,
  Save,
  Edit,
  Plus,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Contract {
  id: string;
  client_id: string;
  service_type: string;
  start_date: string;
  duration_months: number;
  end_date: string;
  value: string | number | null;
  currency: string;
  billing_cycle: string;
  status: string;
  auto_renew: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  periods_missing?: number;
}

interface Client {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string | null;
  industry: string | null;
  status: string;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  creator?: { first_name: string; last_name: string };
  contracts?: Contract[];
}

interface ClientsResponse {
  success: boolean;
  message: string;
  data: Client[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const SERVICE_TYPES = [
  "social_media_management",
  "content_creation",
  "seo",
  "paid_advertising",
  "app_deployment",
  "talent_placement",
  "web_development",
  "consulting",
  "other",
];

const CLIENT_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
};

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  ended: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
  renewed: "bg-blue-100 text-blue-800",
};

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
  if (amount === null || amount === undefined) return `${currency} 0`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return `${currency} 0`;
  if (numAmount >= 1000000000) return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
  if (numAmount >= 1000000) return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
  return `${currency} ${numAmount.toLocaleString()}`;
};

const isExpiringSoon = (contract: Contract) => {
  if (contract.status !== "active") return false;
  const end = new Date(contract.end_date).getTime();
  const now = Date.now();
  const diffDays = (end - now) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 30;
};

const nearestEndDate = (contracts?: Contract[]) => {
  if (!contracts || contracts.length === 0) return null;
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) return null;
  return active.reduce((soonest, c) =>
    new Date(c.end_date) < new Date(soonest.end_date) ? c : soonest
  );
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} position="bottom" heightPercentage={80} showCloseButton={false}>
        <div className="h-full flex flex-col bg-black text-white">{children}</div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface LeadSearchResult {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string | null;
  industry: string | null;
  value: string | number | null;
  currency: string;
}

const AddClientForm = ({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [mode, setMode] = useState<"new" | "existing_lead">("new");
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    industry: "",
    status: "active",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // "From Existing Lead" mode
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState<LeadSearchResult[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadSearchResult | null>(null);
  const [contractForm, setContractForm] = useState({
    service_type: SERVICE_TYPES[0],
    start_date: new Date().toISOString().slice(0, 10),
    duration_months: "6",
    value: "",
    billing_cycle: "monthly",
  });

  useEffect(() => {
    if (mode !== "existing_lead" || !leadSearch.trim()) {
      setLeadResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setIsSearchingLeads(true);
      try {
        const response = await get<{ success: boolean; data: { leads: LeadSearchResult[] } }>(
          `/search?q=${encodeURIComponent(leadSearch)}`
        );
        if (response.success) {
          setLeadResults(response.data.leads || []);
        }
      } catch (error) {
        console.error("Error searching leads:", error);
      } finally {
        setIsSearchingLeads(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [leadSearch, mode]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ company_name: "", contact_person: "", email: "", phone: "", industry: "", status: "active", notes: "" });
    setMode("new");
    setLeadSearch("");
    setLeadResults([]);
    setSelectedLead(null);
    setContractForm({
      service_type: SERVICE_TYPES[0],
      start_date: new Date().toISOString().slice(0, 10),
      duration_months: "6",
      value: "",
      billing_cycle: "monthly",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (mode === "existing_lead") {
        if (!selectedLead) return;
        const response = await put(`/leads/${selectedLead.id}/convert-to-client`, {
          service_type: contractForm.service_type,
          start_date: contractForm.start_date,
          duration_months: parseInt(contractForm.duration_months, 10),
          value: contractForm.value ? parseFloat(contractForm.value) : undefined,
          billing_cycle: contractForm.billing_cycle,
        });
        if (response.success) {
          onSuccess();
          onClose();
          resetForm();
        }
      } else {
        const response = await post("/clients", formData);
        if (response.success) {
          onSuccess();
          onClose();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error creating client:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add New Client</h2>
          <p className="text-xs text-gray-400 mt-1">Create a retainer/service client</p>
        </div>
      </div>

      <div className="px-6 pt-4">
        <div className="flex gap-2 bg-gray-900 border border-gray-700 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              mode === "new" ? "bg-primary text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            New Client
          </button>
          <button
            type="button"
            onClick={() => setMode("existing_lead")}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              mode === "existing_lead" ? "bg-primary text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            From Existing Lead
          </button>
        </div>
      </div>

      {mode === "existing_lead" ? (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <p className="text-xs text-gray-400">
            Search for a lead already in the system — even an old one — and convert it into a client with its first contract.
          </p>

          {!selectedLead ? (
            <>
              <div>
                <label className="text-xs font-semibold text-white mb-2 block">Search Leads *</label>
                <input
                  type="text"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search by company, contact, or email..."
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
                />
              </div>

              {isSearchingLeads && <p className="text-xs text-gray-500">Searching...</p>}

              {leadResults.length > 0 && (
                <div className="space-y-2">
                  {leadResults.map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => setSelectedLead(lead)}
                      className="w-full text-left bg-gray-900 border border-gray-800 rounded-lg p-3 hover:border-primary transition-colors"
                    >
                      <p className="text-xs font-semibold text-white">{lead.company_name}</p>
                      <p className="text-xs text-gray-400">{lead.contact_person} · {lead.email}</p>
                    </button>
                  ))}
                </div>
              )}

              {leadSearch.trim() && !isSearchingLeads && leadResults.length === 0 && (
                <p className="text-xs text-gray-500">No matching leads found.</p>
              )}
            </>
          ) : (
            <>
              <div className="bg-gray-900 rounded-lg p-4 flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-white">{selectedLead.company_name}</p>
                  <p className="text-xs text-gray-400 mt-1">{selectedLead.contact_person} · {selectedLead.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedLead(null)}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Change
                </button>
              </div>

              <div>
                <label className="text-xs font-semibold text-white mb-2 block">Service Type *</label>
                <select
                  value={contractForm.service_type}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, service_type: e.target.value }))}
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                >
                  {SERVICE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {formatLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">Effective Date * <span className="font-normal text-gray-400">(may be in the past)</span></label>
                  <DatePicker dark value={contractForm.start_date} onChange={(v) => setContractForm((prev) => ({ ...prev, start_date: v }))} required />
                </div>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">Duration (months) *</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={contractForm.duration_months}
                    onChange={(e) => setContractForm((prev) => ({ ...prev, duration_months: e.target.value }))}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">Contract Value ({selectedLead.currency})</label>
                  <input
                    type="number"
                    value={contractForm.value}
                    onChange={(e) => setContractForm((prev) => ({ ...prev, value: e.target.value }))}
                    placeholder="Per billing cycle"
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">Billing Cycle</label>
                  <select
                    value={contractForm.billing_cycle}
                    onChange={(e) => setContractForm((prev) => ({ ...prev, billing_cycle: e.target.value }))}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="one_time">One-time</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </form>
      ) : (
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Company Name *</label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Contact Person *</label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Industry</label>
          <input
            type="text"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>
      )}

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            (mode === "new"
              ? !formData.company_name || !formData.contact_person || !formData.email
              : !selectedLead || !contractForm.start_date || !contractForm.duration_months)
          }
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {mode === "existing_lead" ? "Converting..." : "Creating..."}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {mode === "existing_lead" ? "Convert to Client" : "Create Client"}
            </>
          )}
        </button>
        <button
          onClick={() => {
            resetForm();
            onClose();
          }}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditClientForm = ({
  isOpen,
  onClose,
  onSuccess,
  client,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client: Client | null;
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    industry: "",
    status: "active",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        company_name: client.company_name,
        contact_person: client.contact_person,
        email: client.email,
        phone: client.phone || "",
        industry: client.industry || "",
        status: client.status,
        notes: client.notes || "",
      });
    }
  }, [client]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    setIsSubmitting(true);
    try {
      const response = await put(`/clients/${client.id}`, formData);
      if (response.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating client:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!client) return null;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Client</h2>
          <p className="text-xs text-gray-400 mt-1">Update client details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Company Name *</label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Contact Person *</label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Industry</label>
          <input
            type="text"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update Client
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const AddContractForm = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: string | null;
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    service_type: SERVICE_TYPES[0],
    start_date: new Date().toISOString().slice(0, 10),
    duration_months: "6",
    value: "",
    currency: "USD",
    billing_cycle: "monthly",
    auto_renew: false,
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const previewEndDate = () => {
    const start = new Date(formData.start_date);
    if (isNaN(start.getTime())) return null;
    const months = parseInt(formData.duration_months, 10) || 0;
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    return end.toLocaleDateString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    setIsSubmitting(true);
    try {
      const response = await post(`/clients/${clientId}/contracts`, {
        ...formData,
        duration_months: parseInt(formData.duration_months, 10),
      });
      if (response.success) {
        const missing = (response as { data?: { contract?: { periods_missing?: number } } }).data?.contract?.periods_missing ?? 0;
        onSuccess();
        onClose();
        // Contracts entered late: offer to bill the periods that already passed.
        if (missing > 0 && confirm(`This contract's effective date is in the past, so ${missing} billing period${missing === 1 ? " has" : "s have"} already ended without an invoice.\n\nGenerate the backdated invoices now?`)) {
          navigate(`/invoices/client/${clientId}?generate=${(response as { data: { contract: { id: string } } }).data.contract.id}`);
        }
        setFormData({
          service_type: SERVICE_TYPES[0],
          start_date: new Date().toISOString().slice(0, 10),
          duration_months: "6",
          value: "",
          currency: "USD",
          billing_cycle: "monthly",
          auto_renew: false,
          notes: "",
        });
      }
    } catch (error) {
      console.error("Error creating contract:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add Contract</h2>
          <p className="text-xs text-gray-400 mt-1">Attach a service contract to this client</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Service Type *</label>
          <select
            value={formData.service_type}
            onChange={(e) => handleChange("service_type", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {SERVICE_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Effective Date * <span className="font-normal text-gray-400">(may be in the past)</span></label>
            <DatePicker dark value={formData.start_date} onChange={(v) => handleChange("start_date", v)} required />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Duration (months) *</label>
            <input
              type="number"
              min={1}
              required
              value={formData.duration_months}
              onChange={(e) => handleChange("duration_months", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        {previewEndDate() && (
          <p className="text-xs text-gray-400">
            Contract ends: <span className="text-white font-semibold">{previewEndDate()}</span>
          </p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Value</label>
            <input
              type="number"
              value={formData.value}
              onChange={(e) => handleChange("value", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="USD">USD</option>
              <option value="UGX">UGX</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Billing Cycle</label>
          <select
            value={formData.billing_cycle}
            onChange={(e) => handleChange("billing_cycle", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="one_time">One-time</option>
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.auto_renew}
            onChange={(e) => handleChange("auto_renew", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-600 text-primary focus:ring-2 focus:ring-primary"
          />
          <span className="text-xs text-gray-300">Auto-renew</span>
        </label>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.start_date || !formData.duration_months}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Create Contract
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const ClientDrawer = ({
  client,
  isOpen,
  onClose,
  onDelete,
  onEdit,
  onAddContract,
  onDeleteContract,
}: {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (clientId: string) => void;
  onEdit: () => void;
  onAddContract: () => void;
  onDeleteContract: (contractId: string) => void;
}) => {
  if (!client) return null;

  const contracts = client.contracts || [];

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Client Details</h2>
          <p className="text-xs text-gray-400 mt-1">{client.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <div className={`inline-block px-3 py-1 text-xs font-semibold rounded-lg ${CLIENT_STATUS_COLORS[client.status] || "bg-gray-100 text-gray-800"}`}>
            {formatLabel(client.status)}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Company</p>
              <p className="text-xs text-gray-400">{client.company_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Contact Person</p>
              <p className="text-xs text-gray-400">{client.contact_person}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{client.email}</p>
            </div>
          </div>

          {client.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Phone</p>
                <p className="text-xs text-gray-400">{client.phone}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(client.created_at).toLocaleDateString()}
                {client.creator ? ` by ${client.creator.first_name} ${client.creator.last_name}` : ""}
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Contracts ({contracts.length})
            </label>
            <button
              onClick={onAddContract}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          </div>

          {contracts.length === 0 ? (
            <p className="text-xs text-gray-500">No contracts yet.</p>
          ) : (
            <div className="space-y-3">
              {contracts.map((contract) => {
                const expiringSoon = isExpiringSoon(contract);
                return (
                  <div key={contract.id} className="bg-gray-900 rounded-lg p-3 border border-gray-800">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-xs font-semibold text-white">{formatLabel(contract.service_type)}</p>
                      <div className="flex items-center gap-1">
                        {expiringSoon && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <AlertCircle className="w-3 h-3" />
                            Expiring soon
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONTRACT_STATUS_COLORS[contract.status] || "bg-gray-100 text-gray-800"}`}>
                          {formatLabel(contract.status)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(contract.start_date).toLocaleDateString()} → {new Date(contract.end_date).toLocaleDateString()} ({contract.duration_months}mo)
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">
                        {formatCurrency(contract.value, contract.currency)} / {formatLabel(contract.billing_cycle)}
                        {contract.auto_renew && (
                          <span className="ml-2 inline-flex items-center gap-1 text-blue-400">
                            <RefreshCw className="w-3 h-3" /> auto-renew
                          </span>
                        )}
                      </p>
                      <button
                        onClick={() => {
                          if (confirm("Delete this contract?")) onDeleteContract(contract.id);
                        }}
                        className="text-red-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {(contract.periods_missing ?? 0) > 0 && (
                      <Link
                        to={`/invoices/client/${client.id}?generate=${contract.id}`}
                        className="mt-3 block rounded-lg bg-primary px-3 py-2 text-center text-xs font-medium text-white hover:opacity-90 transition"
                      >
                        Generate {contract.periods_missing} backdated invoice{contract.periods_missing === 1 ? "" : "s"}
                      </Link>
                    )}
                    <Link
                      to={`/invoices/client/${client.id}`}
                      className="mt-2 inline-block text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      View invoices & billing periods →
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 border-t border-gray-800">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => {
              if (confirm("Are you sure you want to delete this client?")) {
                onDelete(client.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { client: Client; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <Edit className="w-3 h-3" />
            Edit Client
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery] = useState("");
  const [statusFilter] = useState("all");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientDrawer, setShowClientDrawer] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/clients?page=${pagination.page}&page_size=${pagination.per_page}`;
      const response = await get<ClientsResponse>(url);
      if (response.success) {
        setClients(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
        if (selectedClient) {
          const refreshed = response.data.find((c) => c.id === selectedClient.id);
          if (refreshed) setSelectedClient(refreshed);
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!confirm(`Delete ${rows.length} selected clients? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/clients/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setClients((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    alert(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} clients deleted successfully`);
  };

  const handleDelete = async (clientId: string) => {
    try {
      await del(`/clients/${clientId}`);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      if (selectedClient?.id === clientId) {
        setShowClientDrawer(false);
        setSelectedClient(null);
      }
      alert("Client deleted successfully");
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("Error deleting client");
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    if (!selectedClient) return;
    try {
      await del(`/clients/${selectedClient.id}/contracts/${contractId}`);
      fetchClients();
    } catch (error) {
      console.error("Error deleting contract:", error);
      alert("Error deleting contract");
    }
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.contact_person.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowClientDrawer(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setShowEditClient(true);
  };

  const handleAddSuccess = () => {
    fetchClients();
    alert("Client created successfully");
  };

  const handleEditSuccess = () => {
    fetchClients();
    alert("Client updated successfully");
  };

  const handleContractSuccess = () => {
    fetchClients();
    alert("Contract created successfully");
  };

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: "company_name",
      header: "Client",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.company_name}</p>
          <p className="text-xs text-gray-600">{row.original.contact_person}</p>
        </div>
      ),
    },
    {
      accessorKey: "industry",
      header: "Industry",
      cell: ({ row }) => (
        <p className="text-xs text-gray-700">{row.original.industry ? formatLabel(row.original.industry) : "—"}</p>
      ),
    },
    {
      id: "contracts",
      header: "Contracts",
      cell: ({ row }) => {
        const contracts = row.original.contracts || [];
        const nearest = nearestEndDate(contracts);
        return (
          <div>
            <p className="text-xs text-gray-700">{contracts.length} total</p>
            {nearest && (
              <p className={`text-xs ${isExpiringSoon(nearest) ? "text-amber-600 font-semibold" : "text-gray-500"}`}>
                Next end: {new Date(nearest.end_date).toLocaleDateString()}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CLIENT_STATUS_COLORS[row.original.status] || "bg-gray-100 text-gray-800"}`}>
          {formatLabel(row.original.status)}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <p className="text-xs text-gray-600">{new Date(row.original.created_at).toLocaleDateString()}</p>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          client={row.original}
          onView={() => handleViewClient(row.original)}
          onEdit={() => handleEditClient(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Clients" description="Manage retainer clients and service contracts">
          <button
              onClick={() => setShowAddClient(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200 min-w-[140px]"
            >
              Add Client
            </button>
        </PageHeader>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredClients} onDeleteSelected={handleBatchDelete} onAddRow={() => setShowAddClient(true)} addRowLabel="Add client">
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredClients.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No clients found</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all"
                      ? "Adjust your search criteria or filters to find relevant clients."
                      : "Start tracking retainer clients and their service contracts."}
                  </p>
                  {!searchQuery && statusFilter === "all" && (
                    <button
                      type="button"
                      onClick={() => setShowAddClient(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Add First Client
                    </button>
                  )}
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} clients
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ClientDrawer
        client={selectedClient}
        isOpen={showClientDrawer}
        onClose={() => {
          setShowClientDrawer(false);
          setSelectedClient(null);
        }}
        onDelete={handleDelete}
        onEdit={() => {
          setShowClientDrawer(false);
          setShowEditClient(true);
        }}
        onAddContract={() => setShowAddContract(true)}
        onDeleteContract={handleDeleteContract}
      />

      <AddClientForm isOpen={showAddClient} onClose={() => setShowAddClient(false)} onSuccess={handleAddSuccess} />

      <EditClientForm
        isOpen={showEditClient}
        onClose={() => {
          setShowEditClient(false);
        }}
        onSuccess={handleEditSuccess}
        client={selectedClient}
      />

      <AddContractForm
        isOpen={showAddContract}
        onClose={() => setShowAddContract(false)}
        onSuccess={handleContractSuccess}
        clientId={selectedClient?.id || null}
      />
    </div>
  );
}

export default ClientsPage;
